import styled from "styled-components";
import { colorPalette, typeScale } from "../../utils/theme";

export const TopbarLogo = styled.img`
  width: 125px;
`;

// Navbar Container
export const Navbar = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== "theme" && prop !== "isScrolled",
})<{ theme?: "dark" | "light"; isScrolled?: boolean }>`
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  height: 70px;
  z-index: 10;
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;

  @media (max-width: 992px) {
    background-color: ${({ theme }) =>
      theme === "dark" ? "#040F2B" : "#D2DFF7"};
    ${({ isScrolled, theme }) =>
      isScrolled &&
      `
      border-bottom: ${
        theme === "dark" ? "none" : `1px solid ${colorPalette.blue300}`
      } !important;
    `}
  }

  @media (min-width: 992px) {
    ${({ isScrolled, theme }) =>
      isScrolled &&
      `
      border-bottom: ${
        theme === "dark" ? "none" : `1px solid ${colorPalette.blue300}`
      } !important;
      background-color: ${
        theme === "dark" ? "rgba(23, 34, 77, 0.5)" : "rgba(255,255,255, 0.3)"
      } !important;
      backdrop-filter: blur(20px) !important;
      -webkit-backdrop-filter: blur(20px) !important;
    `}
  }
`;

// Navbar Title
export const NavbarTitle = styled.span`
  color: ${colorPalette.blue600};
  ${typeScale.bodyMDMain}
`;

// Search Container
export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Search Input
export const SearchInput = styled.input`
  background-color: #f3f3f3;
  color: #b1b5c1;
  font-size: 14px;
  padding-left: 40px;
  border: none;
  border-radius: 8px;
  transition: background-color 0.3s;
  height: 40px;

  &:focus {
    background-color: #f3f3f3;
    color: #b1b5c1;
    border: 1px solid #d3d1d1;
    box-shadow: none;
    outline: 0;
  }

  &::placeholder {
    color: #b1b5c1;
  }
`;

// Search Icon
/*export const SearchIcon = styled(BsSearch)`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #b1b5c1;
  z-index: 10;

  @media (max-width: 992px) {
    color: white;
  }
`;*/

// User Avatar
export const AvatarWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 50%; /* Ensures it's always a circle */

  &:hover {
    cursor: pointer;
  }
`;

export const NotificationDot = styled.div`
  position: absolute;
  bottom: 6px; /* Adjusted to be fully visible */
  right: 6px;
  width: 10px; /* Slightly larger for visibility */
  height: 10px;
  background-color: #c0392b; /* Red */
  border-radius: 50%;
  border: 1px solid white; /* Ensures a clear white outline */
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Avatar = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== "showBorder",
})<{ showBorder: boolean }>`
  width: 100%; /* Ensure it scales within wrapper */
  height: 100%;
  object-fit: cover; /* Ensures image does not stretch */
  border-radius: 50%;
  cursor: pointer;

  ${({ showBorder }) =>
    showBorder &&
    `
    border: 3px solid ${colorPalette.blue500};
  `}
`;

// Initials Avatar
export const InitialsAvatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "showBorder",
})<{ showBorder: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${colorPalette.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${colorPalette.blue400};
  ${typeScale.headingXXSMain};

  ${({ showBorder }) =>
    showBorder &&
    `
    border: 3px solid ${colorPalette.blue500};
  `}
`;

// Styled for the Avatar dropdown container
export const AvatarDropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  right: -14px;
  width: 250px;
  background-color: ${colorPalette.white};
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  padding: 8px 0;

  /* Arrow pointing to the avatar */
  &::before {
    content: "";
    position: absolute;
    top: -10px;
    right: 20px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 15px solid transparent;
    border-bottom: 10px solid ${colorPalette.white};
  }
`;

// Individual dropdown items
export const AvatarDropdownItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: ${colorPalette.textMain};
  ${typeScale.bodySMMain};

  &:not(:last-child) {
    border-bottom: 1px solid ${colorPalette.blue200};
  }

  &:hover {
    background-color: ${colorPalette.blue50};
  }
`;

export const DropdownItemLabel = styled.p`
  margin: 0;
  color: ${colorPalette.textMain};
  ${typeScale.bodySMMain};
`;

// Compact language toggle shown in the topbar on mobile, where the avatar
// dropdown (which carries the language switch on desktop) is hidden.
export const LangToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border-radius: 999px;
  padding: 2px;
  background-color: rgba(23, 34, 77, 0.06);
`;

export const LangOption = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 4px 9px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  background-color: ${({ $active }) =>
    $active ? colorPalette.primaryDark : "transparent"};
  color: ${({ $active }) => ($active ? colorPalette.white : colorPalette.textDark)};
  transition: background-color 0.2s, color 0.2s;
`;

export const DropdownHeader = styled.div`
  display: block;
  border-bottom: 1px solid ${colorPalette.blue200};
  padding: 12px 16px;
`;

export const DropdownTitle = styled.h4`
  margin-bottom: 8px;
  color: ${colorPalette.textMain};
  ${typeScale.headingXXSMain}
`;

export const DropdownSubtitle = styled.h4`
  margin: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXXS}
`;

/* PROGRESS BAR */
export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 5px;
  background-color: ${colorPalette.blue50};
  position: relative;
`;

export const Progress = styled.div<{ width: number }>`
  height: 100%;
  background-color: ${colorPalette.blue500};
  width: ${({ width }) => width}%;
  transition: width 0.3s ease;
`;

export const TopbarMenuWrapper = styled.div<{ theme?: "light" | "dark" }>`
  position: relative;
  padding: 4px 6px;
  border-radius: 30px;
  backdrop-filter: blur(100px);
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  gap: 16px;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    z-index: -1;
    inset: 0;
    padding: 1px; /* border thickness */
    border-radius: 30px;
    background: linear-gradient(
      90.48deg,
      ${({ theme }) => (theme === "dark" ? "#ebf1f750" : "#ebf1f7")} 1.56%,
      rgba(235, 241, 247, 0) 13.3%,
      rgba(235, 241, 247, 0) 81.86%,
      ${({ theme }) => (theme === "dark" ? "#ebf1f750" : "#ebf1f7")} 97.37%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

/*
  color={colorPalette.white}
  activeColor={colorPalette.textMain}
  activeBackgroundColor={colorPalette.blue300}
*/

export const TopbarMenuItem = styled.div<{
  theme?: "light" | "dark";
  active?: boolean;
}>`
  padding: 6px 16px;
  border-radius: 30px;
  cursor: pointer;
  ${({ active }) => (active ? typeScale.bodySMMain : typeScale.bodySM)};
  color: ${({ active, theme }) =>
    active
      ? theme === "light"
        ? colorPalette.white
        : colorPalette.textMain
      : theme === "light"
      ? colorPalette.textMain
      : colorPalette.white};
  background-color: ${({ active, theme }) =>
    active
      ? theme === "light"
        ? colorPalette.primaryDark
        : colorPalette.blue300
      : "transparent"};

  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    color: ${({ active, theme }) =>
      active
        ? theme === "light"
          ? colorPalette.white
          : colorPalette.textMain
        : theme === "light"
        ? colorPalette.white
        : colorPalette.textMain};
    background-color: ${({ active, theme }) =>
      active
        ? theme === "light"
          ? colorPalette.primaryDark
          : colorPalette.blue300
        : theme === "dark"
        ? colorPalette.blue300
        : colorPalette.primaryDark};
  }
`;
